import { AppFunctionComponent } from "../../../../../types"
import React, { useEffect, useRef, useState } from "react"
import { TextLink } from "../../../../../ui/components/link/link.styled"
import { PrimaryHeading } from "../../../heading.component"
import { SectionLayout } from "../../section.component"
import { MuditaStoresProps } from "./mudita-stores.interface"
import {
  ContentWrapper,
  StoreTypesWrapper,
  SingleStoreType,
  SingleStore,
  StoreName,
  Details,
  StoresBackgroundWrapper,
  StoresImage,
  SingleStoreWrapper,
} from "./mudita-stores.styled"
import { SliderButtonStyle } from "../quick-start/quick-start.styled"
import {
  ControlsNextIcon,
  ControlsPrevIcon,
} from "../quick-start/quick-start-mobile-controls.component"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import SwiperCore from "swiper"
import { SwiperSlide } from "swiper/react"
import styled from "styled-components"
import { TestimonialsControlsWrapper } from "../../../text-with-embed-carousel-stock/text-with-embed-carousel-stock"

const MapIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="21"
    viewBox="0 0 21 21"
    fill="none"
  >
    <path
      d="M10.5001 12.1667C12.3384 12.1667 13.8334 10.6717 13.8334 8.83332C13.8334 6.99499 12.3384 5.49999 10.5001 5.49999C8.66176 5.49999 7.16676 6.99499 7.16676 8.83332C7.16676 10.6717 8.66176 12.1667 10.5001 12.1667ZM10.5001 7.16666C11.4193 7.16666 12.1668 7.91416 12.1668 8.83332C12.1668 9.75249 11.4193 10.5 10.5001 10.5C9.58092 10.5 8.83343 9.75249 8.83343 8.83332C8.83343 7.91416 9.58092 7.16666 10.5001 7.16666Z"
      fill="#666666"
    />
    <path
      d="M10.0168 18.6783C10.1578 18.779 10.3268 18.8332 10.5001 18.8332C10.6734 18.8332 10.8424 18.779 10.9834 18.6783C11.2368 18.4992 17.1909 14.2 17.1668 8.83332C17.1668 5.15749 14.1759 2.16666 10.5001 2.16666C6.82428 2.16666 3.83345 5.15749 3.83345 8.82916C3.80928 14.2 9.76345 18.4992 10.0168 18.6783ZM10.5001 3.83332C13.2576 3.83332 15.5001 6.07582 15.5001 8.83749C15.5176 12.5358 11.8434 15.8567 10.5001 16.9458C9.15761 15.8558 5.48261 12.5342 5.50011 8.83332C5.50011 6.07582 7.74261 3.83332 10.5001 3.83332Z"
      fill="#666666"
    />
  </svg>
)

const MuditaStores: AppFunctionComponent<MuditaStoresProps> = ({
  config,
  images,
}) => {
  const { text, title, types } = config
  const [swiper, setSwiper] = useState<SwiperCore>()
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const { translate } = useTranslationHook()

  useEffect(() => {
    if (swiper) {
      setActiveIndex(swiper.activeIndex)
    }
  }, [swiper?.activeIndex])

  return (
    <StoresBackgroundWrapper
      layout={SectionLayout.ThreeCols}
      background="#F8FAFC"
    >
      <ContentWrapper>
        <StoresPrimaryHeading>{title}</StoresPrimaryHeading>
        <p>{text}</p>
      </ContentWrapper>

      <StoreTypesWrapper>
        <SingleStoreType
          slidesPerView={1}
          spaceBetween={32}
          ref={ref}
          onSwiper={(swiper) => setSwiper(swiper)}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
          }}
          navigation={{
            nextEl: ".swiper-arrow-right-embed-testimonials",
            prevEl: ".swiper-arrow-left-embed-testimonials",
          }}
        >
          {types[0].stores.map((store, index) => (
            <SwiperSlide key={index}>
              <SingleStoreWrapper>
                <SingleStore>
                  <StoreName>{store.name}</StoreName>
                  <DetailsWrapper>
                    {store.details_one && (
                      <Details
                        dangerouslySetInnerHTML={{
                          __html: store.details_one,
                        }}
                      />
                    )}

                    {store.details_two && (
                      <Details
                        dangerouslySetInnerHTML={{ __html: store.details_two }}
                      />
                    )}
                  </DetailsWrapper>
                  {store.button && (
                    <MapLink to={store.buttonUrl}>
                      <MapIcon />
                      {store.button}
                    </MapLink>
                  )}
                </SingleStore>
                {images && (
                  <StoresImage image={images[index].gatsbyImageData} />
                )}
              </SingleStoreWrapper>
            </SwiperSlide>
          ))}
        </SingleStoreType>

        <TestimonialsControlsWrapper>
          <SliderButtonStyle
            className="swiper-arrow-left-embed-testimonials"
            disabledPrev={activeIndex === 0}
          >
            <ControlsPrevIcon />
            {translate("PREV")}
          </SliderButtonStyle>
          <SliderButtonStyle
            className="swiper-arrow-right-embed-testimonials"
            disabledNext={activeIndex === types[0].stores.length - 1}
          >
            {translate("NEXT")}
            <ControlsNextIcon />
          </SliderButtonStyle>
        </TestimonialsControlsWrapper>
      </StoreTypesWrapper>
    </StoresBackgroundWrapper>
  )
}

export default MuditaStores

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  p {
    text-align: center;
  }
`

const MapLink = styled(TextLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`

const StoresPrimaryHeading = styled(PrimaryHeading)`
  text-align: center;
  max-width: 1280px;
  width: 100%;
`
